/**
 * TanStack Query factory functions for orders.
 *
 * Phase 4a wires these against real Supabase queries (joined). They are pure
 * factories so they can be unit-tested with a mock Supabase client.
 *
 * Two scopes apply naturally via RLS:
 *   - Admin: gets all rows (no clinic filter)
 *   - Podiatrist: gets only their clinic's rows (RLS enforces)
 *
 * Status filter tabs from `phase4-input/ux-feedback.md`:
 *   pending / submitted / production / delivery / delivered / active / total
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';

export type OrderStatusFilter =
  | 'pending'
  | 'submitted'
  | 'production'
  | 'delivery'
  | 'delivered'
  | 'active'
  | 'total';

/**
 * Phase 4c — top-level product family tab on the order tracker.
 * `custom` = orders whose lines include any custom-made line_type
 * `ots`    = orders whose lines include any OTS / ready-made line_type
 * `all`    = no filter
 */
export type OrderFamilyFilter = 'all' | 'custom' | 'ots';

/**
 * Bug-fix B: explicit scoping payload for the /agent surface. The agent
 * tracker MUST narrow at the application layer, not via RLS alone, because
 * the orders RLS policy unions current_clinic_ids() with
 * agent_acquired_clinic_ids(). An agent who is also a clinic_member of
 * Clinic X would otherwise see Clinic X's orders alongside their acquired
 * clinics. Passing this struct forces:
 *   - clinic_id IN acquiredClinicIds (always)
 *   - podiatrist_id <> agentId (when includeOwnOrders is FALSE, the default)
 */
export interface AgentScope {
  agentId: string;
  acquiredClinicIds: string[];
  includeOwnOrders: boolean;
}

export interface OrderListParams {
  /** When set, scope to this clinic only (podiatrist view); otherwise admin sees all. */
  clinicId?: string | null;
  status: OrderStatusFilter;
  /** Phase 4c — family filter applied via inner-join + IN. */
  family?: OrderFamilyFilter;
  /** Free-text: order number or patient name. Resolved SERVER-SIDE against
   *  the whole dataset (fn_search_order_ids), not the loaded page. */
  search?: string | undefined;
  /** False for agents: they must not match on patient names (PDPL). */
  searchIncludesNames?: boolean;
  page?: number;
  pageSize?: number;
  /** Column to sort by. */
  sortBy?: 'created_at' | 'expected_delivery_date' | 'order_number' | 'status' | 'patient';
  sortDir?: 'asc' | 'desc';
  /**
   * Bug-fix B: when present, narrow to the agent's acquired clinics in
   * the application layer (not just RLS) and (unless includeOwnOrders is
   * TRUE) exclude orders where podiatrist_id matches the agent.
   */
  agentScope?: AgentScope | null;
  /**
   * Bug fix (podiatrist strict isolation): when set, narrows the query to
   * orders WHERE podiatrist_id = podiatristId. Used by the /orders surface
   * so a podiatrist only ever sees orders they themselves authored, even on
   * clinics they share with other podiatrists. Agent + admin surfaces leave
   * this null (those scopes have their own visibility rules).
   */
  podiatristId?: string | null;
  /**
   * Recycle bin (20270357). Falsey (the default) lists LIVE orders only.
   * True lists ONLY binned orders, which is the admin bin page. There is
   * deliberately no "show both" mode: mixing binned and live rows in one
   * list is exactly how a deleted order ends up looking live.
   */
  deletedOnly?: boolean;
}

// Exported so the LPO modal's status filter mirrors the page's chips from
// the SAME mapping instead of reimplementing the groupings.
export const ENUM_BY_FILTER: Record<OrderStatusFilter, string[]> = {
  // R28 — simplified status set. in_review / on_hold / cancelled / returned
  // are no longer offered as selectable statuses in the UI. Pending = draft
  // only; Production = in_production only; Active = the live forward path.
  pending: ['draft'],
  submitted: ['submitted'],
  production: ['in_production'],
  delivery: ['in_delivery'],
  delivered: ['delivered'],
  active: ['draft', 'submitted', 'in_production', 'in_delivery'],
  total: [],
};

const CUSTOM_LINE_TYPES = [
  'custom_insole',
  'premium_3d_insole',
  'orthopedic_sandal',
  'sandal_insole_combo',
];
const OTS_LINE_TYPES = ['ots_insole', 'orthopaedic_material', 'other_equipment'];

/**
 * Compact representation of one order line on the tracker row. Used both for
 * the `primary_line` (legacy single-row label) and for the new `lines` array
 * (Task B4/B5: per-line product labels + per-line patient_name fallback).
 */
export interface OrderListLine {
  id: string;
  line_type: string;
  /** Visual product family (pa12_insole, carbon_insole, etc.); null for OTS lines. */
  solution_subtype: string | null;
  /** EU end-to-end vs local-chain production. Null for non-custom lines. */
  production_path: 'eu' | 'local' | null;
  short_code: string | null;
  /** True when the line has an ACTIVE patient QR token (NFC tag minted). */
  has_active_token: boolean;
  /** Optional patient name captured on the wizard's marketplace branches. */
  patient_name: string | null;
  /** Pairs/units on the line (order_lines.quantity, default 1). */
  quantity: number;
  /**
   * Wizard JSONB blob. Includes display_label / product_name / sub_branch /
   * material for the various branches; used by formatProductLabel() to pick
   * the most specific human label.
   */
  configuration: Record<string, unknown>;
  product: { id: string; name_en: string; sku: string } | null;
  assigned_provider: { id: string; name: string } | null;
}

export interface OrderListRow {
  id: string;
  order_number: string;
  status: string;
  total_aed: number;
  created_at: string;
  expected_delivery_date: string | null;
  podiatrist_id: string;
  clinic_id: string;
  patient: { id: string; full_name: string } | null;
  podiatrist: { id: string; full_name: string } | null;
  clinic: { id: string; name: string; slug: string | null } | null;
  primary_line: OrderListLine | null;
  /**
   * All lines on the order (ordered by line_number). Needed by the tracker
   * UI so it can fall back to a line.patient_name when the order has no
   * top-level patient (marketplace flows).
   */
  lines: OrderListLine[];
  /**
   * Direct-billing flag (R-direct-billing). When true, the order is billed
   * by the podiatrist directly to the patient (no clinic invoice). The
   * tracker renders a "Direct Billing" badge in the Clinic cell and the
   * admin detail page shows a Mark-as-paid block.
   */
  is_direct_billed: boolean;
  /**
   * ISO timestamp when an admin marked the direct-billed order as paid.
   * Null while the order is still awaiting payment. Production transitions
   * are gated until this is set (enforced server-side by the state-machine
   * trigger).
   */
  direct_billing_paid_at: string | null;
  /**
   * When true, zoho-create-invoice refuses to invoice this order's clinic. Set
   * when an order the patient already paid (direct billing) is reassigned to a
   * clinic, so the clinic is not double-billed. Cleared only by an admin from
   * this detail page (override_order_zoho_block).
   */
  zoho_invoice_blocked: boolean;
  /**
   * Item 7 wizard draft-completeness flag. `true` = every required
   * prescription field is present; `false` = incomplete (the tracker must not
   * flip it draft->submitted); `null` = legacy / ungated (rows predating the
   * column, or orders the new wizard never saved as a draft). Only an explicit
   * `false` blocks the tracker flip, enforced authoritatively server-side.
   */
  draft_complete: boolean | null;
  /** Visual urgent marker (orders.is_urgent): tints the row; no side effects. */
  is_urgent: boolean;
  /**
   * Recycle bin (migration 20270357). Null means live. Non-null means the
   * order sits in the admin-only bin. The orders SELECT policy hides binned
   * rows from every non-admin, so a podiatrist never receives one of these
   * at all; for an admin it is what the bin page renders.
   */
  deleted_at: string | null;
  deleted_by: string | null;
}

export const ordersKeys = {
  all: ['orders'] as const,
  lists: () => [...ordersKeys.all, 'list'] as const,
  list: (params: OrderListParams) => [...ordersKeys.lists(), params] as const,
  detail: (orderId: string) => [...ordersKeys.all, 'detail', orderId] as const,
  /** Reorder-by-name search (wizard "Existing patient" flow). */
  reorderSearch: (term: string, limit: number) =>
    [...ordersKeys.all, 'reorder-search', term, limit] as const,
  history: (orderId: string) => [...ordersKeys.all, 'history', orderId] as const,
  /**
   * Admin "Stalled drafts" surface (public.admin_stranded_drafts view).
   * Prefixed with ordersKeys.all so the existing broad
   * invalidateQueries({ queryKey: ['orders'] }) calls refresh it too.
   */
  strandedList: () => [...ordersKeys.all, 'stranded', 'list'] as const,
  strandedCount: () => [...ordersKeys.all, 'stranded', 'count'] as const,
  messages: (orderId: string) => [...ordersKeys.all, 'messages', orderId] as const,
  files: (orderId: string) => [...ordersKeys.all, 'files', orderId] as const,
  statusCounts: (
    clinicId: string | null | undefined,
    family: OrderFamilyFilter | null | undefined,
    /**
     * Bug-fix B: agentScope is part of the cache key so the /agent
     * tracker's per-status counts invalidate independently from the
     * podiatrist + admin trackers (different scoping rules).
     */
    agentScope?: AgentScope | null,
    /**
     * Bug fix (podiatrist strict isolation): part of the cache key so the
     * /orders surface's per-status counts invalidate independently from
     * admin and from other podiatrists looking at the same clinic.
     */
    podiatristId?: string | null,
  ) =>
    [
      ...ordersKeys.all,
      'status-counts',
      clinicId ?? null,
      family ?? 'all',
      agentScope
        ? {
            agentId: agentScope.agentId,
            // Sort so cache key is order-stable; the input may be in any order.
            acquiredClinicIds: [...agentScope.acquiredClinicIds].sort(),
            includeOwnOrders: agentScope.includeOwnOrders,
          }
        : null,
      podiatristId ?? null,
    ] as const,
};

/* -------------------------------------------------------------------------- */
/*  Per-status row counts (used by the OrderTrackerPage status filter boxes)  */
/* -------------------------------------------------------------------------- */

export interface OrdersStatusCounts {
  pending: number;
  submitted: number;
  in_production: number;
  in_delivery: number;
  delivered: number;
}

/**
 * Per-status count query for the orders page status filter boxes.
 *
 * Returns one count per enum value (pending=draft, submitted, in_production,
 * in_delivery, delivered). The consumer derives:
 *   - active = pending + submitted + in_production + in_delivery
 *   - total  = active + delivered
 *
 * Task B3: counts now respect the currently-selected family tab. When family
 * is 'custom' or 'ots' we first resolve the matching order_ids via
 * resolveFamilyOrderIds (same helper as ordersListQuery), then add an
 * .in('id', orderIds) filter to each per-status head-count request. When the
 * resolved id list is empty we short-circuit with all-zero counts so we
 * don't waste 5 round trips.
 *
 * Clinic scoping mirrors ordersListQuery (filter by clinic_id when provided;
 * otherwise wide view for admin/agent and RLS handles the security boundary).
 *
 * Runs as up to 5 parallel head-count queries; cheap enough for the small
 * enum set.
 */
export function ordersStatusCountsQuery(
  client: SupabaseClient<Database>,
  {
    clinicId,
    family,
    agentScope,
    podiatristId,
  }: {
    clinicId?: string | null;
    family?: OrderFamilyFilter | null;
    /**
     * Bug-fix B: when present, narrow the counts to the agent's
     * acquired clinics and (by default) exclude orders the agent
     * placed themselves. Mirrors ordersListQuery.
     */
    agentScope?: AgentScope | null;
    /**
     * Bug fix (podiatrist strict isolation): when present, narrow the
     * counts to orders WHERE podiatrist_id = podiatristId so the /orders
     * surface only ever reflects the viewer's own orders, even on clinics
     * shared with other podiatrists.
     */
    podiatristId?: string | null;
  },
) {
  return {
    queryKey: ordersKeys.statusCounts(
      clinicId,
      family ?? 'all',
      agentScope ?? null,
      podiatristId ?? null,
    ),
    queryFn: async (): Promise<OrdersStatusCounts> => {
      // Bug-fix B: an agent with zero acquired clinics has zero
      // visible orders. Short-circuit before any DB round trip.
      if (agentScope && agentScope.acquiredClinicIds.length === 0) {
        return {
          pending: 0,
          submitted: 0,
          in_production: 0,
          in_delivery: 0,
          delivered: 0,
        };
      }

      // Resolve the family filter to a set of order IDs first. Null result
      // = no filter; empty array = filter matches nothing → all-zero counts.
      const familyIds = await resolveFamilyOrderIds(
        client,
        family ?? undefined,
        clinicId,
      );
      if (familyIds !== null && familyIds.length === 0) {
        return {
          pending: 0,
          submitted: 0,
          in_production: 0,
          in_delivery: 0,
          delivered: 0,
        };
      }

      const countFor = async (statusValue: string): Promise<number> => {
        // The generated `status` column type is the union of legacy enum
        // values; cast at the boundary so we can call eq() with a plain
        // string literal here without dragging the union into the public API.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let q: any = client
          .from('orders')
          .select('id', { count: 'exact', head: true })
          .eq('status', statusValue as never)
          // The status chips build their OWN query instead of going through
          // buildListQuery, so the bin filter has to be repeated here. Without
          // this line the chips would keep counting orders the list beneath
          // them has already hidden, for admins specifically (the policy
          // carve-out lets admins see binned rows).
          .is('deleted_at', null);
        if (clinicId) {
          q = q.eq('clinic_id', clinicId);
        }
        if (familyIds !== null) {
          q = q.in('id', familyIds);
        }
        // Bug-fix B: apply agent narrowing. RLS would let through any
        // clinic the agent belongs to via clinic_members; we narrow
        // here so the surface is purely "agent-acquired clinics".
        if (agentScope) {
          q = q.in('clinic_id', agentScope.acquiredClinicIds);
          if (!agentScope.includeOwnOrders) {
            q = q.neq('podiatrist_id', agentScope.agentId);
          }
        }
        // Podiatrist strict isolation: narrow to orders the viewer
        // themselves authored. RLS would otherwise expose every order
        // on each clinic the viewer is a member of (including orders
        // authored by other podiatrists at the same clinic).
        if (podiatristId) {
          q = q.eq('podiatrist_id', podiatristId);
        }
        const { count, error } = await q;
        if (error) throw error;
        return (count as number | null) ?? 0;
      };

      const [pending, submitted, in_production, in_delivery, delivered] =
        await Promise.all([
          countFor('draft'),
          countFor('submitted'),
          countFor('in_production'),
          countFor('in_delivery'),
          countFor('delivered'),
        ]);

      return { pending, submitted, in_production, in_delivery, delivered };
    },
  };
}

/**
 * Builder used by both list query + count query, so they share filtering logic.
 * The select string includes joined relations using PostgREST embedding syntax.
 */
function buildListQuery(client: SupabaseClient<Database>, params: OrderListParams) {
  const select =
    'id, order_number, status, total_aed, created_at, expected_delivery_date,' +
    ' podiatrist_id, clinic_id, is_direct_billed, prices_include_vat, direct_billing_paid_at, zoho_invoice_blocked, draft_complete, is_urgent, deleted_at, deleted_by,' +
    ' patient:patients(id, full_name),' +
    ' podiatrist:profiles!orders_podiatrist_profile_fkey(id, full_name),' +
    ' clinic:clinics(id, name, slug),' +
    // Task B4/B5: line.configuration + line.patient_name are needed by the
    // tracker UI to render per-line product labels and the patient fallback
    // for marketplace flows where the order has no top-level patient.
    ' lines:order_lines(id, line_number, line_type, solution_subtype, production_path,' +
    '   patient_name, quantity, configuration,' +
    '   product:products(id, name_en, sku),' +
    '   assigned_provider:providers(id, name),' +
    '   qr_tokens:patient_qr_tokens(short_code, state))';

  // We can't pass `(count: 'exact')` and a typed Database row type cleanly because
  // the eq() typings reject the synthetic `patient` join column; cast at the boundary.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let q = client.from('orders').select(select, { count: 'exact' }) as any;

  // Recycle bin (20270357). The RLS policy already hides binned orders from
  // every non-admin, so this is the ADMIN-side filter: it keeps a normal
  // admin list clean, because an admin is the one role the policy carve-out
  // lets see deleted rows. The bin page is the only caller that flips it.
  if (params.deletedOnly) {
    q = q.not('deleted_at', 'is', null);
  } else {
    q = q.is('deleted_at', null);
  }

  if (params.clinicId) {
    q = q.eq('clinic_id', params.clinicId);
  }

  // Bug-fix B: narrow to the agent's acquired clinics in the app layer.
  // Relying on RLS alone would surface orders from clinics the agent is
  // also a clinic_member of (orders RLS unions current_clinic_ids with
  // agent_acquired_clinic_ids), which is NOT what the /agent surface
  // should show. We also (by default) exclude orders the agent placed
  // themselves as podiatrist; that gate is opt-out via includeOwnOrders.
  if (params.agentScope) {
    q = q.in('clinic_id', params.agentScope.acquiredClinicIds);
    if (!params.agentScope.includeOwnOrders) {
      q = q.neq('podiatrist_id', params.agentScope.agentId);
    }
  }

  // Podiatrist strict isolation: when set, narrow the query to orders
  // the viewer themselves authored. The /orders surface uses this so a
  // podiatrist who belongs to a clinic alongside other podiatrists only
  // sees their own orders. Agent + admin surfaces leave this null.
  if (params.podiatristId) {
    q = q.eq('podiatrist_id', params.podiatristId);
  }

  const enumValues = ENUM_BY_FILTER[params.status];
  if (enumValues.length > 0) {
    q = q.in('status', enumValues);
  }

  // NOTE: search is NOT filtered here. It is resolved to order ids by
  // fn_search_order_ids and applied as `.in('id', ...)` in ordersListQuery.
  // The prefix `order_number.ilike` filter that used to live here was dead
  // code (nothing populated params.search) until the tracker started passing
  // the term - at which point PostgREST ANDed it with the id list and every
  // patient-name search returned nothing. Caught in review before it shipped.

  const sortBy = params.sortBy ?? 'created_at';
  const ascending = (params.sortDir ?? 'desc') === 'asc';
  if (sortBy === 'patient') {
    // Sort by patient name via foreign relation
    q = q.order('full_name', { foreignTable: 'patients', ascending });
  } else {
    q = q.order(sortBy, { ascending });
  }

  // Cap raised from 100 → 5000 so the column-filter popover (R28) and the
  // CSV export can fetch the full clinic dataset for client-side filtering.
  const pageSize = Math.max(1, Math.min(params.pageSize ?? 25, 5000));
  const page = Math.max(0, params.page ?? 0);
  const from = page * pageSize;
  const to = from + pageSize - 1;
  q = q.range(from, to);

  return { query: q, page, pageSize };
}

function escapeLike(s: string): string {
  return s.replace(/[%_]/g, (m) => `\\${m}`);
}

/** Prefer the active token's short_code, else the first token present. */
function extractShortCode(tokens: unknown): string | null {
  if (!Array.isArray(tokens) || tokens.length === 0) return null;
  const list = tokens as Array<{ short_code?: string; state?: string } | null>;
  const active = list.find((t) => t?.state === 'active');
  return (active ?? list[0])?.short_code ?? null;
}

/** True when any embedded token is in the `active` state. */
function hasActiveToken(tokens: unknown): boolean {
  if (!Array.isArray(tokens)) return false;
  return (tokens as Array<{ state?: string } | null>).some((t) => t?.state === 'active');
}

export interface OrderListResult {
  rows: OrderListRow[];
  total: number;
  page: number;
  pageSize: number;
}

/** Maps a raw joined order_line row to the OrderListLine shape we expose. */
function toListLine(l: Record<string, unknown>): OrderListLine {
  return {
    id: String(l.id),
    line_type: String(l.line_type),
    solution_subtype: (l.solution_subtype ?? null) as string | null,
    production_path: (l.production_path ?? null) as 'eu' | 'local' | null,
    short_code: extractShortCode(l.qr_tokens),
    has_active_token: hasActiveToken(l.qr_tokens),
    patient_name: (l.patient_name ?? null) as string | null,
    quantity: Number(l.quantity ?? 1),
    configuration: (l.configuration ?? {}) as Record<string, unknown>,
    product: (l.product ?? null) as { id: string; name_en: string; sku: string } | null,
    assigned_provider: (l.assigned_provider ?? null) as { id: string; name: string } | null,
  };
}

/** Maps a raw joined row to the typed OrderListRow shape we expose. */
function toListRow(row: Record<string, unknown>): OrderListRow {
  const lines = (row.lines ?? []) as Array<Record<string, unknown>> | null;
  const sorted = (lines ?? [])
    .slice()
    .sort((a, b) => Number(a.line_number ?? 0) - Number(b.line_number ?? 0));
  const mapped = sorted.map(toListLine);
  const first = mapped[0] ?? null;
  return {
    id: String(row.id),
    order_number: String(row.order_number),
    status: String(row.status),
    total_aed: Number(row.total_aed ?? 0),
    created_at: String(row.created_at),
    expected_delivery_date: (row.expected_delivery_date ?? null) as string | null,
    podiatrist_id: String(row.podiatrist_id),
    clinic_id: String(row.clinic_id),
    patient: (row.patient ?? null) as OrderListRow['patient'],
    podiatrist: (row.podiatrist ?? null) as OrderListRow['podiatrist'],
    clinic: (row.clinic ?? null) as OrderListRow['clinic'],
    primary_line: first,
    lines: mapped,
    is_direct_billed: Boolean(row.is_direct_billed),
    direct_billing_paid_at: (row.direct_billing_paid_at ?? null) as string | null,
    zoho_invoice_blocked: Boolean(row.zoho_invoice_blocked),
    // Cast through unknown: generated database.types.ts may not yet carry
    is_urgent: (row as Record<string, unknown>).is_urgent === true,
    // Recycle bin (20270357). Null for every live order.
    deleted_at: ((row as Record<string, unknown>).deleted_at ?? null) as string | null,
    deleted_by: ((row as Record<string, unknown>).deleted_by ?? null) as string | null,
    // draft_complete (added by migration 20270215000000). null = legacy/ungated.
    draft_complete: ((row as Record<string, unknown>).draft_complete ?? null) as
      | boolean
      | null,
  };
}

/**
 * Resolve the set of order_ids matching the given family filter. Returns null
 * to indicate "no filter applies" (family = 'all' or undefined). Returns
 * an empty array to indicate "filter applies but matches zero orders" — the
 * caller short-circuits the main list query.
 */
/**
 * Resolve the ids of every order matching a free-text search, ACROSS THE WHOLE
 * DATASET rather than the loaded page (Rafael, 2026-08-10: "I could not find
 * order 2600777 for exactly this reason").
 *
 * Mirrors resolveFamilyOrderIds: a cheap id pass, then `.in('id', ids)` on the
 * real list query, so every existing filter, sort and the exact count keep
 * working unchanged.
 *
 * Matching is fn_search_order_ids, SECURITY INVOKER, so RLS decides what a
 * caller may match on. includeNames=false (agents) drops the patient-name
 * arms: an agent must not learn a name exists from a hit count.
 */
/**
 * How many matching ids we will push through `.in('id', ...)`.
 *
 * PostgREST takes filters in the query string, and a uuid costs ~38 bytes
 * once encoded, so 2,000 ids is a ~78KB URL that gateways reject outright.
 * 300 keeps the URL near 11KB, and the RPC returns the NEWEST matches first,
 * so a truncated search still shows the orders someone is most likely hunting.
 */
const SEARCH_ID_CAP = 300;

async function resolveSearchOrderIds(
  client: SupabaseClient<Database>,
  search: string | undefined,
  includeNames: boolean,
): Promise<string[] | null> {
  const term = (search ?? '').trim();
  if (term.length === 0) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (client.rpc as any)('fn_search_order_ids', {
    p_term: term,
    p_include_names: includeNames,
    p_limit: SEARCH_ID_CAP,
  });
  if (error) throw error;
  const rows = (data ?? []) as Array<string | { fn_search_order_ids: string }>;
  return rows.map((r) => (typeof r === 'string' ? r : r.fn_search_order_ids));
}

async function resolveFamilyOrderIds(
  client: SupabaseClient<Database>,
  family: OrderFamilyFilter | undefined,
  clinicId: string | null | undefined,
): Promise<string[] | null> {
  if (!family || family === 'all') return null;
  const types = family === 'custom' ? CUSTOM_LINE_TYPES : OTS_LINE_TYPES;
  // When scoping by clinic we must embed the `orders` relation in the
  // select string before PostgREST will accept a filter on
  // `orders.clinic_id` (otherwise it returns PGRST108: "'orders' is not
  // an embedded resource"). The `!inner` modifier turns the embed into an
  // INNER JOIN so rows whose parent order is NULL or mismatched get
  // filtered out, which matches our intent. The admin / wideView path
  // (clinicId == null) skips the filter and keeps a flat select.
  const select = clinicId ? 'order_id, orders!inner(clinic_id)' : 'order_id';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let q: any = client
    .from('order_lines')
    .select(select)
    // The line_type values are union enum strings; cast at the typing boundary
    // because the strict generated type rejects a plain string[] literal.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .in('line_type', types as any);
  if (clinicId) {
    q = q.eq('orders.clinic_id', clinicId);
  }
  const { data, error } = await q;
  if (error) throw error;
  const ids: string[] = Array.from(
    new Set(
      (data ?? []).map((r: { order_id: string }) => String(r.order_id)),
    ),
  );
  return ids;
}

export function ordersListQuery(client: SupabaseClient<Database>, params: OrderListParams) {
  return {
    queryKey: ordersKeys.list(params),
    queryFn: async (): Promise<OrderListResult> => {
      // Bug-fix B: agent with zero acquired clinics -> empty result, no
      // DB round trip. Mirrors ordersStatusCountsQuery's short circuit.
      if (params.agentScope && params.agentScope.acquiredClinicIds.length === 0) {
        return { rows: [], total: 0, page: params.page ?? 0, pageSize: params.pageSize ?? 25 };
      }

      // Resolve the family filter to a set of order IDs first (when applicable).
      const familyIds = await resolveFamilyOrderIds(client, params.family, params.clinicId);
      if (familyIds !== null && familyIds.length === 0) {
        return { rows: [], total: 0, page: params.page ?? 0, pageSize: params.pageSize ?? 25 };
      }

      // Same shape for the free-text search: resolve ids across the WHOLE
      // dataset, then filter the real query by them.
      const searchIds = await resolveSearchOrderIds(
        client,
        params.search,
        params.searchIncludesNames ?? true,
      );
      if (searchIds !== null && searchIds.length === 0) {
        return { rows: [], total: 0, page: params.page ?? 0, pageSize: params.pageSize ?? 25 };
      }
      // Both are id lists: intersect, or a second .in() would widen the first.
      const idFilter =
        familyIds !== null && searchIds !== null
          ? familyIds.filter((id) => searchIds.includes(id))
          : (familyIds ?? searchIds);
      if (idFilter !== null && idFilter.length === 0) {
        return { rows: [], total: 0, page: params.page ?? 0, pageSize: params.pageSize ?? 25 };
      }

      const { query, page, pageSize } = buildListQuery(client, params);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let q: any = query;
      if (idFilter !== null) {
        q = q.in('id', idFilter);
      }
      const { data, error, count } = await q;
      if (error) throw error;
      const rows: OrderListRow[] = ((data ?? []) as Array<Record<string, unknown>>).map(toListRow);
      return { rows, total: count ?? rows.length, page, pageSize };
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  Reorder search — find past orders by the patient name on the line         */
/* -------------------------------------------------------------------------- */

/**
 * One match returned by {@link searchOrdersByPatientName}: a single past
 * order_line whose `patient_name` matched the search term, plus enough of its
 * parent order to render a pick-list row and to prefill a new draft from.
 *
 * Context: the patients table is intentionally empty for migrated history —
 * each historical order carries the name on `order_lines.patient_name`, not a
 * `patients` row. So the wizard's "Existing patient" flow can no longer search
 * `patients` (it finds nothing). It searches *orders* by line.patient_name and
 * lets the operator pick a specific past order to prefill the prescription
 * from. This is read-only: nothing here writes, and picking never mutates the
 * past order.
 *
 * Scoping is via RLS exactly like the order tracker: a podiatrist only sees
 * their own clinic's orders; an admin sees every clinic. We add no explicit
 * clinic filter — RLS already does the per-role narrowing.
 */
export interface OrderPatientMatch {
  order_id: string;
  order_number: string;
  status: string;
  created_at: string;
  clinic: { id: string; name: string } | null;
  podiatrist: { id: string; full_name: string } | null;
  /** Resolved patient name (the matched line.patient_name). */
  patient_name: string;
  /** `orders.patient_id`, when the order header is linked to a record. */
  order_patient_id: string | null;
  /**
   * `order_lines.patient_id`, when the LINE is linked to a record.
   *
   * ensure_line_patient (the QR path) mints a patients row and sets this
   * WITHOUT ever setting the header's patient_id, so an order can be linked
   * here and unlinked there. Ignoring this key is why the same person shows up
   * twice in the wizard search today.
   */
  line_patient_id: string | null;
  /** The matched line — carries `configuration` for the prescription prefill. */
  line: OrderListLine;
}

export interface SearchOrdersByPatientNameParams {
  search: string;
  /** Max parent orders to fetch (each may contribute >1 matching line row). */
  limit?: number;
}

/**
 * Search past orders by the patient name stored on their lines. Returns a
 * flat list of `OrderPatientMatch` (one per matching line) ordered newest
 * first, so several orders for the same person each appear as their own row.
 */
export function searchOrdersByPatientName(
  client: SupabaseClient<Database>,
  params: SearchOrdersByPatientNameParams,
) {
  const term = (params.search ?? '').trim();
  const limit = Math.max(1, Math.min(params.limit ?? 20, 50));
  return {
    queryKey: ordersKeys.reorderSearch(term, limit),
    queryFn: async (): Promise<OrderPatientMatch[]> => {
      // Require a couple of characters so a stray keystroke doesn't scan the
      // whole table; the caller also debounces.
      if (term.length < 2) return [];
      // `order_lines!inner` makes the embed an INNER JOIN, so an order only
      // comes back when it has a matching line; filtering on the embedded
      // `lines.patient_name` then restricts the returned `lines` array to
      // just the rows that matched. RLS on `orders` scopes the whole query
      // to the caller's clinic(s) (admin sees all) — same as the tracker.
      const select =
        // patient_id (order header AND line) is what lets the wizard tell a
        // record-backed order from a record-less one, so the search can show one
        // row per person instead of the same human twice.
        'id, order_number, status, created_at, patient_id,' +
        ' clinic:clinics(id, name),' +
        ' podiatrist:profiles!orders_podiatrist_profile_fkey(id, full_name),' +
        ' lines:order_lines!inner(id, line_number, line_type, solution_subtype,' +
        '   production_path, patient_name, patient_id, quantity, configuration,' +
        '   product:products(id, name_en, sku),' +
        '   assigned_provider:providers(id, name),' +
        '   qr_tokens:patient_qr_tokens(short_code))';
      const pattern = `%${escapeLike(term)}%`;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let q: any = client.from('orders').select(select);
      q = q.ilike('lines.patient_name', pattern);
      q = q.order('created_at', { ascending: false }).limit(limit);
      const { data, error } = await q;
      if (error) throw error;

      const out: OrderPatientMatch[] = [];
      const needle = term.toLowerCase();
      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        const lines = ((row.lines ?? []) as Array<Record<string, unknown>>)
          .slice()
          .sort((a, b) => Number(a.line_number ?? 0) - Number(b.line_number ?? 0));
        for (const raw of lines) {
          const line = toListLine(raw);
          const name = (line.patient_name ?? '').trim();
          // Defensive: keep only lines that actually contain the term, in case
          // PostgREST ever returns sibling lines on the same matched order.
          if (!name || !name.toLowerCase().includes(needle)) continue;
          out.push({
            order_id: String(row.id),
            order_number: String(row.order_number),
            status: String(row.status),
            created_at: String(row.created_at),
            clinic: (row.clinic ?? null) as OrderPatientMatch['clinic'],
            podiatrist: (row.podiatrist ?? null) as OrderPatientMatch['podiatrist'],
            patient_name: name,
            // Either key linking this order to a patients row. ensure_line_patient
            // sets the LINE one without ever setting the header one, which is
            // why both are carried.
            order_patient_id: (row.patient_id ?? null) as string | null,
            line_patient_id: (raw.patient_id ?? null) as string | null,
            line,
          });
        }
      }
      return out;
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  Order detail                                                              */
/* -------------------------------------------------------------------------- */

export interface OrderDetail {
  id: string;
  order_number: string;
  status: string;
  total_aed: number;
  subtotal_aed: number;
  tax_aed: number;
  created_at: string;
  submitted_at: string | null;
  expected_delivery_date: string | null;
  actual_delivery_date: string | null;
  delivery_address: string | null;
  admin_observations: string | null;
  notes: string | null;
  podiatrist_id: string;
  clinic_id: string;
  /**
   * Direct-billing flag (R-direct-billing). When true, the order is billed
   * by the podiatrist directly to the patient. The admin detail page
   * shows a Mark-as-paid block and production transitions are gated until
   * the order is marked paid.
   */
  is_direct_billed: boolean;
  /**
   * TRUE when order_lines.unit_price_aed already INCLUDES 5% VAT (the figure
   * agreed with the patient in direct billing); FALSE when VAT is added on top
   * (clinic pricing). This is the PRICE BASIS and it is deliberately NOT the
   * same thing as `is_direct_billed`, which is the billing AUDIENCE:
   * reassign_order_billing flips the audience without re-pricing the lines, and
   * QR reorders are direct-billed but store net. Any UI that says whether VAT is
   * included must read THIS, never is_direct_billed.
   */
  prices_include_vat: boolean;
  /**
   * ISO timestamp when an admin marked the direct-billed order as paid.
   * Null while the order is still awaiting payment.
   */
  direct_billing_paid_at: string | null;
  /**
   * When true, zoho-create-invoice refuses to invoice this order's clinic. Set
   * when an order the patient already paid (direct billing) is reassigned to a
   * clinic, so the clinic is not double-billed. Cleared only by an admin from
   * the order detail page (override_order_zoho_block).
   */
  zoho_invoice_blocked: boolean;
  /**
   * Stripe Checkout Session id minted by the
   * create-direct-billing-checkout Edge Function. Null until the first
   * payment link is created. The DirectBillingPaymentCard uses presence of
   * this to flip the button copy from "Send payment link" to "Copy payment
   * link". The hosted Stripe URL follows the pattern
   * https://checkout.stripe.com/c/pay/<session_id> and may expire; pressing
   * "Send payment link" again mints a fresh session and overwrites this
   * column.
   */
  stripe_checkout_session_id: string | null;
  /**
   * Hosted Stripe Checkout URL persisted at session-mint time by the
   * create-direct-billing-checkout Edge Function (migration
   * 20261125000000_direct_billing_checkout_url). Null on legacy rows minted
   * before that migration; the FE falls back to an Edge Function call in
   * that case. Used by DirectBillingPaymentCard so "Copy payment link"
   * never round-trips to Stripe on the single-order path.
   */
  stripe_checkout_url: string | null;
  /**
   * Foreign key to direct_billing_payment_groups.id when this order is part
   * of a combined payment group. Null otherwise. Powers the Combine Orders
   * banner on DirectBillingPaymentCard.
   */
  payment_group_id: string | null;
  /**
   * Stamped by the stripe-webhook when a payment discrepancy was recorded:
   * amount mismatches (item 7) and DOUBLE COLLECTIONS - a payment arriving
   * for an order already marked paid (item 30). Non-null means an admin must
   * reconcile; OrderDetailPage renders a loud banner from it.
   */
  payment_mismatch_at: string | null;
  payment_mismatch_note: string | null;
  /**
   * Zoho invoice reference (orders.zoho_invoice_id). Non-null means a
   * machine-raised invoice exists and money corrections must go through a
   * credit note, never a line edit. NOTE the platform has never machine-
   * invoiced (all-null in prod as of 2026-09-03) and hand-made Zoho
   * invoices leave this null, so a null here does NOT prove the order is
   * uninvoiced in Zoho Books.
   */
  zoho_invoice_id: string | null;
  /**
   * orders.client_payment_status ('unbilled' | 'invoiced' | 'paid' | ...).
   * 'invoiced' and 'paid' both freeze quantity edits in the prescription
   * dialog: money already collected or documented goes through a credit
   * note, not an edit.
   */
  client_payment_status: string;
  patient: {
    id: string;
    full_name: string;
    gender: string | null;
    /** ISO date string (YYYY-MM-DD) when captured; null otherwise. Surfaced
     *  here so the Workshop PDF can render a formatted DOB without an extra
     *  patient lookup. */
    date_of_birth: string | null;
    email: string | null;
    phone: string | null;
    /** Free-text patient note (patients.notes). Single source of truth: an edit
     *  to the patient's note flows into any future regenerated prescription
     *  Excel. Null for legacy record-less orders (patient_id NULL). */
    notes: string | null;
  } | null;
  podiatrist: { id: string; full_name: string; email: string } | null;
  clinic: { id: string; name: string; slug: string | null } | null;
  lines: Array<{
    id: string;
    line_number: number;
    line_type: string;
    /** Visual product family (pa12_insole, carbon_insole, etc.); null for OTS lines. */
    solution_subtype: string | null;
    /** EU end-to-end vs local-chain production. Null for non-custom lines. */
    production_path: 'eu' | 'local' | null;
    /** Optional free-text patient name on a ready-made / FITO line. */
    patient_name: string | null;
    short_code: string | null;
    status: string;
    configuration: Record<string, unknown>;
    modifications: Array<{
      region: string;
      modification_type: string;
      intensity?: 'light' | 'medium' | 'strong' | null;
      parameters?: Record<string, unknown>;
    }>;
    unit_price_aed: number;
    quantity: number;
    line_total_aed: number;
    submitted_at: string | null;
    delivered_at: string | null;
    product: { id: string; name_en: string; sku: string } | null;
    assigned_provider: { id: string; name: string } | null;
  }>;
}

export function orderDetailQuery(client: SupabaseClient<Database>, orderId: string) {
  return {
    queryKey: ordersKeys.detail(orderId),
    queryFn: async (): Promise<OrderDetail | null> => {
      const select =
        '*,' +
        ' patient:patients(id, full_name, gender, date_of_birth, email, phone, notes),' +
        ' podiatrist:profiles!orders_podiatrist_profile_fkey(id, full_name, email),' +
        ' clinic:clinics(id, name, slug),' +
        ' lines:order_lines(' +
        '   id, line_number, line_type, solution_subtype, production_path, patient_name, status, configuration, modifications,' +
        '   unit_price_aed, quantity, line_total_aed, submitted_at, delivered_at,' +
        '   product:products(id, name_en, sku),' +
        '   assigned_provider:providers(id, name),' +
        '   qr_tokens:patient_qr_tokens(short_code)' +
        ' )';
      const { data, error } = await client
        .from('orders')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .select(select as any)
        .eq('id', orderId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as unknown as Record<string, unknown>;
      const lines = (row.lines ?? []) as Array<Record<string, unknown>>;
      lines.sort((a, b) => Number(a.line_number ?? 0) - Number(b.line_number ?? 0));
      return {
        id: String(row.id),
        order_number: String(row.order_number),
        status: String(row.status),
        total_aed: Number(row.total_aed ?? 0),
        subtotal_aed: Number(row.subtotal_aed ?? 0),
        tax_aed: Number(row.tax_aed ?? 0),
        created_at: String(row.created_at),
        submitted_at: (row.submitted_at ?? null) as string | null,
        expected_delivery_date: (row.expected_delivery_date ?? null) as string | null,
        actual_delivery_date: (row.actual_delivery_date ?? null) as string | null,
        delivery_address: (row.delivery_address ?? null) as string | null,
        admin_observations: (row.admin_observations ?? null) as string | null,
        notes: (row.notes ?? null) as string | null,
        podiatrist_id: String(row.podiatrist_id),
        clinic_id: String(row.clinic_id),
        is_direct_billed: Boolean(row.is_direct_billed),
        prices_include_vat: Boolean(row.prices_include_vat),
        direct_billing_paid_at: (row.direct_billing_paid_at ?? null) as string | null,
        // New column from migration 20270202000000_reassign_order_billing.
        zoho_invoice_blocked: Boolean((row as Record<string, unknown>).zoho_invoice_blocked),
        // New column from migration 20261113000000_direct_billing_stripe_columns.
        // Cast through `unknown` because generated database.types.ts may not
        // yet include the column on the orders Row (regenerate post-migration).
        stripe_checkout_session_id:
          ((row as Record<string, unknown>).stripe_checkout_session_id ?? null) as string | null,
        stripe_checkout_url:
          ((row as Record<string, unknown>).stripe_checkout_url ?? null) as string | null,
        payment_group_id:
          ((row as Record<string, unknown>).payment_group_id ?? null) as string | null,
        payment_mismatch_at:
          ((row as Record<string, unknown>).payment_mismatch_at ?? null) as string | null,
        payment_mismatch_note:
          ((row as Record<string, unknown>).payment_mismatch_note ?? null) as string | null,
        zoho_invoice_id:
          ((row as Record<string, unknown>).zoho_invoice_id ?? null) as string | null,
        client_payment_status: String(
          (row as Record<string, unknown>).client_payment_status ?? 'unbilled',
        ),
        patient: (row.patient ?? null) as OrderDetail['patient'],
        podiatrist: (row.podiatrist ?? null) as OrderDetail['podiatrist'],
        clinic: (row.clinic ?? null) as OrderDetail['clinic'],
        lines: lines.map((l) => ({
          id: String(l.id),
          line_number: Number(l.line_number ?? 0),
          line_type: String(l.line_type),
          solution_subtype: (l.solution_subtype ?? null) as string | null,
          production_path: (l.production_path ?? null) as 'eu' | 'local' | null,
          patient_name: (l.patient_name ?? null) as string | null,
          short_code: extractShortCode(l.qr_tokens),
          status: String(l.status),
          configuration: (l.configuration ?? {}) as Record<string, unknown>,
          modifications: Array.isArray(l.modifications)
            ? (l.modifications as OrderDetail['lines'][number]['modifications'])
            : [],
          unit_price_aed: Number(l.unit_price_aed ?? 0),
          quantity: Number(l.quantity ?? 1),
          line_total_aed: Number(l.line_total_aed ?? 0),
          submitted_at: (l.submitted_at ?? null) as string | null,
          delivered_at: (l.delivered_at ?? null) as string | null,
          product: (l.product ?? null) as OrderDetail['lines'][number]['product'],
          assigned_provider: (l.assigned_provider ??
            null) as OrderDetail['lines'][number]['assigned_provider'],
        })),
      };
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  Order status history (timeline)                                           */
/* -------------------------------------------------------------------------- */

export interface OrderHistoryRow {
  id: string;
  order_id: string | null;
  order_line_id: string | null;
  from_status: string | null;
  to_status: string;
  changed_at: string;
  changed_by: string | null;
  reason: string | null;
  actor: { id: string; full_name: string } | null;
}

export function orderHistoryQuery(client: SupabaseClient<Database>, orderId: string) {
  return {
    queryKey: ordersKeys.history(orderId),
    queryFn: async (): Promise<OrderHistoryRow[]> => {
      const select =
        'id, order_id, order_line_id, from_status, to_status, changed_at, changed_by, reason,' +
        ' actor:profiles!order_status_history_changed_by_profile_fkey(id, full_name)';

      // PostgREST or() doesn't support embedded subqueries. Run two queries and merge:
      //   - rows referencing the order header directly
      //   - rows referencing any line on this order
      const linesRes = await client.from('order_lines').select('id').eq('order_id', orderId);
      if (linesRes.error) throw linesRes.error;
      const lineIds = (linesRes.data ?? []).map((r) => r.id);

      const [orderRes, lineHistRes] = await Promise.all([
        client
          .from('order_status_history')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .select(select as any)
          .eq('order_id', orderId)
          .order('changed_at', { ascending: true }),
        lineIds.length
          ? client
              .from('order_status_history')
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .select(select as any)
              .in('order_line_id', lineIds)
              .order('changed_at', { ascending: true })
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (orderRes.error) throw orderRes.error;
      if ('error' in lineHistRes && lineHistRes.error) throw lineHistRes.error;
      return [
        ...((orderRes.data ?? []) as unknown as OrderHistoryRow[]),
        ...((lineHistRes.data ?? []) as unknown as OrderHistoryRow[]),
      ].sort((a, b) => a.changed_at.localeCompare(b.changed_at));
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  Order messages thread                                                     */
/* -------------------------------------------------------------------------- */

export interface OrderMessageRow {
  id: string;
  order_id: string;
  order_line_id: string | null;
  sender_id: string;
  sender_role: string;
  message: string;
  created_at: string;
  sender: { id: string; full_name: string } | null;
}

export function orderMessagesQuery(client: SupabaseClient<Database>, orderId: string) {
  return {
    queryKey: ordersKeys.messages(orderId),
    queryFn: async (): Promise<OrderMessageRow[]> => {
      const select =
        'id, order_id, order_line_id, sender_id, sender_role, message, created_at,' +
        ' sender:profiles!order_messages_sender_profile_fkey(id, full_name)';
      const { data, error } = await client
        .from('order_messages')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .select(select as any)
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as OrderMessageRow[];
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  Order line files                                                          */
/* -------------------------------------------------------------------------- */

export interface OrderFileRow {
  id: string;
  order_line_id: string;
  file_kind: string;
  storage_bucket: string;
  storage_path: string;
  file_name: string;
  file_size_bytes: number;
  mime_type: string;
  uploaded_at: string;
  thumbnail_path: string | null;
}

export function orderFilesQuery(client: SupabaseClient<Database>, orderId: string) {
  return {
    queryKey: ordersKeys.files(orderId),
    queryFn: async (): Promise<OrderFileRow[]> => {
      // Get all file rows whose order_line is on this order
      const { data: lines, error: linesErr } = await client
        .from('order_lines')
        .select('id')
        .eq('order_id', orderId);
      if (linesErr) throw linesErr;
      const lineIds = (lines ?? []).map((l) => l.id);
      if (lineIds.length === 0) return [];
      const { data, error } = await client
        .from('order_line_files')
        .select(
          'id, order_line_id, file_kind, storage_bucket, storage_path, file_name,' +
            ' file_size_bytes, mime_type, uploaded_at, thumbnail_path',
        )
        .in('order_line_id', lineIds)
        .eq('is_archived', false)
        .order('uploaded_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as OrderFileRow[];
    },
  };
}

/**
 * Generate a 1-hour signed URL for a file in a Supabase Storage bucket.
 * Used by the Files panel on the order detail page.
 */
export async function getSignedUrlFor(
  client: SupabaseClient<Database>,
  bucket: string,
  path: string,
  expiresInSeconds = 3600,
): Promise<string | null> {
  const { data, error } = await client.storage.from(bucket).createSignedUrl(path, expiresInSeconds);
  if (error) return null;
  return data?.signedUrl ?? null;
}

/* -------------------------------------------------------------------------- */
/*  Stalled drafts (admin) - public.admin_stranded_drafts view                 */
/* -------------------------------------------------------------------------- */

/**
 * One row of the public.admin_stranded_drafts view: a draft order that is
 * older than 24 hours and has ZERO order_status_history rows (never touched
 * by the state machine since creation). The view is security_invoker, so RLS
 * applies: admins see every clinic's rows, everyone else gets an empty set.
 */
export interface StrandedDraftRow {
  id: string;
  order_number: string;
  clinic_id: string;
  clinic_name: string;
  podiatrist_id: string;
  podiatrist_name: string | null;
  created_at: string;
  updated_at: string;
  line_count: number;
  total_aed: number;
}

/**
 * List every stalled draft, oldest first (longest-stalled at the top).
 *
 * The view is not yet in the generated database.types.ts (gen-types is a
 * known follow-up chore), hence the `as any` cast at the from() boundary --
 * same pattern as catalog_card_visibility / lead_campaigns.
 */
export async function listStrandedDrafts(
  client: SupabaseClient<Database>,
): Promise<StrandedDraftRow[]> {
  const { data, error } = await client
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('admin_stranded_drafts' as any)
    .select(
      'id, order_number, clinic_id, clinic_name, podiatrist_id, podiatrist_name,' +
        ' created_at, updated_at, line_count, total_aed',
    )
    .order('created_at', { ascending: true });
  if (error) throw error;
  return ((data ?? []) as unknown as Array<Record<string, unknown>>).map((r) => ({
    id: String(r.id),
    order_number: String(r.order_number),
    clinic_id: String(r.clinic_id),
    clinic_name: String(r.clinic_name ?? ''),
    podiatrist_id: String(r.podiatrist_id),
    podiatrist_name: (r.podiatrist_name ?? null) as string | null,
    created_at: String(r.created_at),
    updated_at: String(r.updated_at),
    line_count: Number(r.line_count ?? 0),
    total_aed: Number(r.total_aed ?? 0),
  }));
}

/**
 * Head count of stalled drafts. Drives the informational chip on the admin
 * order tracker; cheap enough to keep fresh alongside the status-box counts.
 */
export async function countStrandedDrafts(
  client: SupabaseClient<Database>,
): Promise<number> {
  const { count, error } = await client
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('admin_stranded_drafts' as any)
    .select('id', { count: 'exact', head: true });
  if (error) throw error;
  return (count as number | null) ?? 0;
}

/* -------------------------------------------------------------------------- */
/*  Mutations — admin status transitions                                       */
/* -------------------------------------------------------------------------- */

export async function setLineStatus(
  client: SupabaseClient<Database>,
  orderLineId: string,
  to: string,
): Promise<void> {
  const { error } = await client
    .from('order_lines')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .update({ status: to } as any)
    .eq('id', orderLineId);
  if (error) throw error;
}

export async function postOrderMessage(
  client: SupabaseClient<Database>,
  args: {
    orderId: string;
    senderId: string;
    senderRole: 'admin' | 'podiatrist' | 'provider';
    message: string;
  },
): Promise<void> {
  const payload = {
    order_id: args.orderId,
    sender_id: args.senderId,
    sender_role: args.senderRole,
    message: args.message,
  } as unknown as Database['public']['Tables']['order_messages']['Insert'];
  const { error } = await client.from('order_messages').insert(payload);
  if (error) throw error;
}
